import {
  FC,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { classes, style } from "typestyle";
import { Role } from "@hulanbv/platformapp";
import { useHistory } from "react-router-dom";
import { routes } from "../../state/common/constants/routes.constants";
import { GeometricShape } from "../elements/geometric-shape";
import { dictionary } from "../../state/common/constants/dictionary.constants";
import { SvgIcon } from "../elements/svg-icon.element";
import { ReactComponent as ssoLoginGraphic } from "../../assets/svgs/chart.svg";
import { ReactComponent as mindgrapherGraphic } from "../../assets/graphics/symbols/mindgrapher-icon.svg";
import { graphicSet } from "../../constants/graphic-set.constants";
import { branding } from "../../constants/branding.constants";
import { ssoTokensService } from "../../state/sso-tokens.service";
import { useAuthContext } from "../../state/authentication/authentication.context";
import { InjectionService } from "../../state/common/injection.service";

interface IProps {
  attributes?: HTMLAttributes<HTMLDivElement>;
}

export const HomeProfessionalTemplate: FC<IProps> = (props) => {
  const history = useHistory();
  const { session } = useAuthContext();
  const isPractitioner = session?.user?.role === Role.PRACTITIONER;

  const [callToActions, setCallToActions] = useState<ReactNode[]>([]);

  const handleSsoClick = useCallback(async () => {
    if (!branding.features?.externalPractitionerSso) {
      return;
    }

    // gather SSO token
    const { data: ssoToken } = await ssoTokensService.request();

    // redirect to SSO URL with token
    InjectionService.openWebView(
      `${branding.features.externalPractitionerSso.url}?ssoToken=${ssoToken.token}`,
    );
  }, []);

  useEffect(() => {
    const hydrateCallToActions = async () => {
      const actions = [
        {
          label: dictionary.literals.trainings,
          icon: graphicSet.trainingsGraphic,
          color: "rgb(var(--rgb-color-primair-lighter))",
          onClick: () => history.push(routes.trainings.path),
          condition: () => true,
        },
        {
          label: dictionary.literals.playground,
          icon: graphicSet.playgroundGraphic,
          color: "rgb(var(--rgb-color-primair-basis))",
          onClick: () => history.push(routes.playground.path),
          condition: () => true,
        },
        {
          label: "MG Assessments",
          icon: mindgrapherGraphic,
          color: "rgb(var(--rgb-color-primair-shade-1))",
          onClick: () => history.push(routes.mindgrapherContent.path),
          condition: () => branding.features?.hasMindgrapherContent,
        },
        {
          label:
            branding.features?.externalPractitionerSso?.title || "SSO Login",
          icon: ssoLoginGraphic,
          color: "rgb(var(--rgb-color-primair-shade-1))",
          onClick: handleSsoClick,
          condition: () => branding.features?.externalPractitionerSso,
        },
        {
          label: dictionary.literals.eLearning,
          icon: graphicSet.eLearningGraphic,
          color: "rgb(var(--rgb-color-primair-shade-4))",
          onClick: () => history.push(routes.elearning.path),
          condition: () => true,
        },
      ];

      setCallToActions(
        actions
          .filter((action) => action.condition())
          .map((action, index) => (
            <div
              key={index}
              className={styles.geometricShapeContainer}
              onClick={action.onClick}
            >
              <GeometricShape
                color={action.color}
                className={styles.geometricShape}
              >
                <SvgIcon icon={action.icon} width={40} height={40} />
              </GeometricShape>
              <div className={styles.geometricShapeLabel}>{action.label}</div>
            </div>
          )),
      );
    };

    hydrateCallToActions();
  }, [handleSsoClick, history, isPractitioner]);

  return (
    <div
      {...props.attributes}
      className={classes(styles.container, props.attributes?.className)}
    >
      <div
        className={classes(
          styles.navButtonsContainer,
          branding.features?.hasMindgrapherContent && styles.paddingForChildren,
        )}
      >
        {callToActions}
      </div>
    </div>
  );
};

const styles = {
  container: style({
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-vertical-regular)",
  }),
  navButtonsContainer: style({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-around",
  }),
  paddingForChildren: style({
    $nest: {
      "& > div": {
        padding: "0 var(--spacing-vertical-regular)",
      },
    },
  }),
  // end top level div containers
  geometricShapeContainer: style({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    maxWidth: "50%",
    alignItems: "center",
    cursor: "pointer",
    marginBottom: 10,
  }),
  geometricShape: style({
    flex: 5,
  }),
  geometricShapeLabel: style({
    flex: 1,
    textAlign: "center",
    fontSize: 17,
    paddingTop: 10,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "100%",
  }),
};
